import { create } from 'zustand';
import type { DeviceAction } from '../api/types';
import { mockAPI, mockScripts } from '../api/mockApi';

interface ScriptState {
  scripts: DeviceAction[];
  currentScript: DeviceAction | null;
  isLoading: boolean;
  error: string | null;
  
  fetchScripts: () => Promise<void>;
  fetchScript: (scriptId: string) => Promise<void>;
  addScript: (script: Partial<DeviceAction>) => Promise<boolean>;
  editScript: (script: DeviceAction) => Promise<boolean>;
  deleteScript: (scriptId: string) => Promise<boolean>;
  startScript: (scriptId: string) => Promise<boolean>;
  toggleScript: (scriptId: string, isActive: boolean) => Promise<boolean>;
  setCurrentScript: (script: DeviceAction | null) => void;
  updateScriptInList: (script: DeviceAction) => void;
  clearError: () => void;
}

export const useScriptStore = create<ScriptState>((set, get) => ({
  scripts: [],
  currentScript: null,
  isLoading: false,
  error: null,

  fetchScripts: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const result = await mockAPI.getScriptsList(1);
      set({ 
        scripts: result.scriptList || [],
        isLoading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch scripts',
        isLoading: false 
      });
    }
  },

  fetchScript: async (scriptId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const result = await mockAPI.getAction(1, scriptId);
      if (result.script) {
        set({ 
          currentScript: result.script,
          isLoading: false 
        });
      }
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch script',
        isLoading: false 
      });
    }
  },

  addScript: async (script: Partial<DeviceAction>) => {
    set({ isLoading: true, error: null });
    
    try {
      const scriptToAdd = { ...script, flatId: 1, actionId: `script_${Date.now()}`, id: `script_${Date.now()}` } as DeviceAction;
      await mockAPI.addAction(scriptToAdd);
      
      // Refresh scripts list
      await get().fetchScripts();
      set({ isLoading: false });
      
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to add script',
        isLoading: false 
      });
      return false;
    }
  },

  editScript: async (script: DeviceAction) => {
    set({ isLoading: true, error: null });
    
    try {
      const scriptToEdit = { ...script, flatId: 1 };
      await mockAPI.editAction(scriptToEdit);
      
      const { scripts } = get();
      const updatedScripts = scripts.map(s => 
        s.actionId === script.actionId ? script : s
      );
      
      set({ 
        scripts: updatedScripts,
        currentScript: script,
        isLoading: false 
      });
      
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to edit script',
        isLoading: false 
      });
      return false;
    }
  },

  deleteScript: async (scriptId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const scriptToDelete = mockScripts.find(s => s.actionId === scriptId);
      if (scriptToDelete) {
        await mockAPI.deleteAction(scriptToDelete);
      }
      
      const { scripts } = get();
      const updatedScripts = scripts.filter(s => s.actionId !== scriptId);
      
      set({ 
        scripts: updatedScripts,
        currentScript: null,
        isLoading: false 
      });
      
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to delete script',
        isLoading: false 
      });
      return false;
    }
  },

  startScript: async (scriptId: string) => {
    try {
      await mockAPI.startScript(1, scriptId);
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to run script'
      });
      return false;
    }
  },

  toggleScript: async (scriptId: string, isActive: boolean) => {
    try {
      const { scripts } = get();
      const script = scripts.find(s => s.actionId === scriptId);
      if (script) {
        const updatedScript = { ...script, isActive };
        await mockAPI.editAction(updatedScript);
        
        const updatedScripts = scripts.map(s => 
          s.actionId === scriptId ? updatedScript : s
        );
        set({ scripts: updatedScripts });
      }
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to toggle script'
      });
      return false;
    }
  },

  setCurrentScript: (script: DeviceAction | null) => {
    set({ currentScript: script });
  },

  updateScriptInList: (script: DeviceAction) => {
    const { scripts } = get();
    const updatedScripts = scripts.map(s => 
      s.actionId === script.actionId ? script : s
    );
    
    set({ scripts: updatedScripts });
  },

  clearError: () => {
    set({ error: null });
  }
}));
